import React, { useState } from 'react';
import HomeLayout from '../../hocs/layout/HomeLayout';
import { Meta } from '../../components/navigation/Helmet';
import { AlertTriangle, Mail, CheckCircle, XCircle } from 'lucide-react';

function DeleteAccount() {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  const handleDeleteRequest = () => {
    setShowConfirmation(true);
  };

  const confirmDelete = () => {
    // Simular proceso de eliminación
    setTimeout(() => {
      setStatus('success');
      setShowConfirmation(false);
    }, 1500);
  };

  return (
    <HomeLayout>
      <Meta title="Eliminar tu cuenta" />

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
            {/* Header */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
              Eliminar tu cuenta
            </h1>
            <p className="text-lg text-gray-600 mb-8 text-center">
              Elimina permanentemente tu cuenta de TableChat
            </p>

            {/* Descripción */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
              <p className="text-gray-700">
                Cuando elimines tu cuenta, tus datos personales, historial de pedidos y preferencias se eliminarán de nuestros sistemas. Esta acción no se puede deshacer. Esta página aplica a la app TableChat (desarrollador: Oval Campus INC).
              </p>
            </div>

            {/* Advertencia */}
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8 flex items-start">
              <AlertTriangle className="text-red-500 mr-3 flex-shrink-0 mt-1" size={24} />
              <div>
                <p className="font-semibold text-red-800">Advertencia: Esta acción es irreversible.</p>
                <p className="text-red-700 mt-1">
                  Una vez completado el proceso, tu cuenta y datos eliminables no podrán recuperarse.
                </p>
              </div>
            </div>

            {/* Contacto */}
            <section className="mb-8 p-6 bg-gray-50 rounded-lg">
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">Contáctanos</h2>
              <p className="text-gray-700 mb-2">
                Si tienes dudas antes de eliminar tu cuenta, escríbenos a
              </p>
              <a href="mailto:contact@tablechat.com" className="text-blue-600 hover:text-blue-800 font-semibold flex items-center">
                <Mail size={18} className="mr-2" />
                contact@tablechat.com
              </a>
            </section>

            {/* Qué se eliminará */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Qué se eliminará</h2>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 mt-1">•</span>
                  <span className="text-gray-700">Tu perfil e información personal</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 mt-1">•</span>
                  <span className="text-gray-700">Historial de pedidos, pagos y preferencias</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 mt-1">•</span>
                  <span className="text-gray-700">Restaurantes favoritos y listas guardadas</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 mt-1">•</span>
                  <span className="text-gray-700">Datos de suscripción administrados por TableChat (debes cancelar las suscripciones externas por separado)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 mt-1">•</span>
                  <span className="text-gray-700">Mensajes y comunicaciones vinculadas a tu cuenta dentro del servicio</span>
                </li>
              </ul>
            </section>

            {/* Pasos para eliminar */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Pasos para eliminar tu cuenta</h2>
              
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg mb-4">
                <h3 className="font-semibold text-gray-800 mb-2">Desde la aplicación:</h3>
                <p className="text-gray-700">
                  Abre la app, ve a Configuración → Ajustes Generales → Eliminar cuenta y sigue los pasos. Confirma tu identidad (PIN/biometría) y envía la solicitud.
                </p>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-teal-50 p-6 rounded-lg mb-4">
                <h3 className="font-semibold text-gray-800 mb-2">Por correo electrónico:</h3>
                <p className="text-gray-700">
                  También puedes solicitar la eliminación enviando un correo a <strong>contact@tablechat.com</strong> con el asunto «Eliminar mi cuenta», indicando el correo registrado y tu país.
                </p>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <p className="text-gray-700">
                  <strong>Verificación:</strong> Podemos solicitar verificación adicional para proteger tu cuenta.
                </p>
                <p className="text-gray-700 mt-2">
                  <strong>Tiempo de procesamiento:</strong> La eliminación de cuenta se procesa en un plazo de hasta 30 días. Las copias de seguridad pueden tardar hasta 90 días en purgarse de forma segura.
                </p>
              </div>
            </section>

            {/* Datos que pueden conservarse */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Datos que pueden conservarse</h2>
              <p className="text-gray-700 mb-4">
                Podemos conservar parte de la información únicamente por motivos legales, de seguridad o cumplimiento:
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Tipo de dato</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Motivo</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Periodo de conservación</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 text-gray-700">Registros de seguridad y antifraude</td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700">Prevención, investigación y seguridad</td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700">Hasta 12 meses</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 text-gray-700">Comprobantes contables y fiscales</td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700">Obligaciones legales/tributarias</td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700">Hasta 5 años (según jurisdicción)</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 text-gray-700">Tickets de soporte y correos</td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700">Atención, defensa de reclamaciones</td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700">Hasta 24 meses</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 text-gray-700">Copias de seguridad</td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700">Integridad y recuperación ante desastres</td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700">Hasta 90 días</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-600 text-sm mt-4 italic">
                Conservamos el mínimo necesario exigido por ley; el resto se elimina o anonimiza.
              </p>
            </section>

            {/* Suscripciones y facturación */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Suscripciones y facturación</h2>
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 space-y-2">
                <p className="text-gray-700">
                  <strong>Importante:</strong> Eliminar tu cuenta no cancela automáticamente compras o suscripciones realizadas en tiendas externas.
                </p>
                <p className="text-gray-700">
                  Cancela las suscripciones directamente desde tu cuenta de App Store o Google Play.
                </p>
                <p className="text-gray-700">
                  No almacenamos datos completos de tarjetas en nuestros sistemas.
                </p>
              </div>
            </section>

            {/* Mensajes de estado */}
            {status === 'success' && (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 flex items-start">
                <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={24} />
                <p className="text-green-800 font-semibold">
                  Tu cuenta ha sido eliminada correctamente.
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 flex items-start">
                <XCircle className="text-red-500 mr-3 flex-shrink-0" size={24} />
                <p className="text-red-800 font-semibold">
                  Hubo un error al eliminar tu cuenta. Inténtalo de nuevo más tarde.
                </p>
              </div>
            )}

            {/* Botón de eliminación */}
            {!status && (
              <div className="text-center">
                <button
                  onClick={handleDeleteRequest}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
                >
                  Eliminar mi cuenta
                </button>
              </div>
            )}

            {/* Modal de confirmación */}
            {showConfirmation && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
                  <div className="flex items-center mb-4">
                    <AlertTriangle className="text-red-500 mr-3" size={32} />
                    <h3 className="text-xl font-bold text-gray-900">Confirmación requerida</h3>
                  </div>
                  <p className="text-gray-700 mb-6">
                    ¿Seguro que quieres eliminar tu cuenta de forma permanente?
                  </p>
                  <div className="flex space-x-4">
                    <button
                      onClick={confirmDelete}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
                    >
                      Sí, eliminar mi cuenta
                    </button>
                    <button
                      onClick={() => setShowConfirmation(false)}
                      className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="border-t border-gray-200 pt-6 mt-8 text-center text-gray-600 text-sm">
              <p>Última actualización: 7 de octubre de 2025</p>
            </div>
          </div>
        </div>
      </div>
    </HomeLayout>
  );
}

export default DeleteAccount;